import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscoveryJobsContext = createContext(null);

export const useDiscoveryJobs = () => useContext(DiscoveryJobsContext);

export const DiscoveryJobsProvider = ({ children }) => {
  // Map of campaignId -> { jobId, status, progress_message, ... }
  const [activeJobs, setActiveJobs] = useState({});
  const pollingTimers = useRef({});

  // Cleanup all polling on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingTimers.current).forEach(clearTimeout);
    };
  }, []);

  const pollJobStatus = useCallback((campaignId, jobId) => {
    // Clear existing timer for this campaign
    if (pollingTimers.current[campaignId]) {
      clearTimeout(pollingTimers.current[campaignId]);
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/discovery-jobs?campaign_id=${campaignId}`, { withCredentials: true });
        const jobs = res.data || [];
        const job = jobs.find(j => j.job_id === jobId) || jobs[0];

        if (!job) return;

        setActiveJobs(prev => ({
          ...prev,
          [campaignId]: {
            jobId: job.job_id,
            status: job.status,
            progressMessage: job.progress_message || '',
            leadsFound: job.leads_found || 0,
            leadsSoFar: job.leads_so_far || 0,
            leadsScraped: job.leads_scraped || 0,
            leadsPendingReview: job.leads_pending_review || 0,
            reviewId: job.review_id || null,
            sourcesCompleted: job.sources_completed || [],
            currentSource: job.current_source || '',
          }
        }));

        if (job.status === 'completed' || job.status === 'failed' || job.status === 'review') {
          delete pollingTimers.current[campaignId];
          return;
        }

        // Continue polling
        pollingTimers.current[campaignId] = setTimeout(checkStatus, 3000);
      } catch (err) {
        // Continue polling on error
        pollingTimers.current[campaignId] = setTimeout(checkStatus, 5000);
      }
    };

    pollingTimers.current[campaignId] = setTimeout(checkStatus, 500);
  }, []);

  const startJob = useCallback(async (campaignId) => {
    try {
      const res = await axios.post(`${API}/campaigns/${campaignId}/run`, {}, { withCredentials: true });
      const jobId = res.data.job_id;

      setActiveJobs(prev => ({
        ...prev,
        [campaignId]: {
          jobId,
          status: 'running',
          progressMessage: 'Starting discovery...',
          leadsFound: 0,
          leadsSoFar: 0,
          leadsScraped: 0,
          sourcesCompleted: [],
          currentSource: '',
        }
      }));

      pollJobStatus(campaignId, jobId);
      return res.data;
    } catch (err) {
      throw err;
    }
  }, [pollJobStatus]);

  const resumePolling = useCallback((campaignId, jobId) => {
    pollJobStatus(campaignId, jobId);
  }, [pollJobStatus]);

  const clearJob = useCallback((campaignId) => {
    if (pollingTimers.current[campaignId]) {
      clearTimeout(pollingTimers.current[campaignId]);
      delete pollingTimers.current[campaignId];
    }
    setActiveJobs(prev => {
      const next = { ...prev };
      delete next[campaignId];
      return next;
    });
  }, []);

  const getJobForCampaign = useCallback((campaignId) => {
    return activeJobs[campaignId] || null;
  }, [activeJobs]);

  const contextValue = useMemo(() => ({
    activeJobs,
    startJob,
    resumePolling,
    clearJob,
    getJobForCampaign,
  }), [activeJobs, startJob, resumePolling, clearJob, getJobForCampaign]);

  return (
    <DiscoveryJobsContext.Provider value={contextValue}>
      {children}
    </DiscoveryJobsContext.Provider>
  );
};
